import React, { useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { Eye, EyeOff, LogIn } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Login() {
  const { t } = useTranslation();
  const { login, user, checked } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  if (checked && user) {
    const to = location.state?.from?.pathname || "/dashboard";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success(t("login.welcomeToast"));
      const to = location.state?.from?.pathname || "/dashboard";
      navigate(to, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden pg-sidebar">
        <div className="absolute inset-0 dot-grid opacity-30" />
        <div
          className="absolute -right-40 -top-40 w-[600px] h-[600px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(91,192,222,0.18), transparent 60%)" }}
        />
        <div
          className="absolute -left-32 -bottom-32 w-[500px] h-[500px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(245,166,35,0.14), transparent 60%)" }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
          <div>
            <img
              src="/assets/logo-light.png"
              alt="Auto-on Solutions"
              className="h-16 mb-12 logo-glow object-contain"
            />
            <div className="text-[12px] tracking-[0.5em] text-cyan-300 uppercase mb-3">
              {t("login.tagline")}
            </div>
            <h1 className="text-5xl xl:text-6xl font-extrabold leading-[1.05]">
              POS<span className="text-amber-400">2</span>GTO
              <br />
              <span className="text-brand-grad">{t("login.heroTitle")}</span>
            </h1>
            <p className="mt-6 text-white/70 max-w-md text-[15px] leading-relaxed">
              {t("login.heroSubtitle")}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6 max-w-md">
            <Stat label={t("login.statReportTypes")} value="4" />
            <Stat label={t("login.statAutoPoll")} value="5 min" />
            <Stat label={t("login.statStores")} value="∞" />
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 grid place-items-center px-6 py-10 bg-[#f0f4f9] relative">
        {/* Language switcher — top-right of the form pane */}
        <div className="absolute top-5 right-5">
          <LanguageSwitcher variant="login" />
        </div>

        <div className="w-full max-w-md pg-card p-8 lg:p-10 anim-in" data-testid="login-card">
          <div className="lg:hidden flex justify-center mb-6">
            <img
              src="/assets/logo-dark.png"
              alt="Auto-on Solutions"
              className="h-14 object-contain"
            />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-1">{t("login.welcome")}</h2>
          <p className="text-sm text-slate-500 mb-7">{t("login.signInTo")}</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="field-label">{t("common.username")}</label>
              <input
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition"
                placeholder={t("login.usernamePlaceholder")}
                data-testid="login-username"
              />
            </div>
            <div>
              <label className="field-label">{t("common.password")}</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-10 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                  aria-label="Toggle password visibility"
                >
                  {show ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold flex items-center justify-center gap-2 transition disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading ? t("login.signingIn") : (<><LogIn size={17} /> {t("login.signIn")}</>)}
            </button>
          </form>

          <div className="mt-8 text-center text-xs text-slate-400">
            © {t("login.footer", { year: new Date().getFullYear() })}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-3xl font-extrabold text-white">{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-white/50 mt-1">{label}</div>
    </div>
  );
}
